import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHome, FiBook, FiDownload, FiUpload, FiUsers, FiUserCheck, FiSettings, FiShield, FiBriefcase, FiDatabase, FiInbox } = FiIcons;

export default function Sidebar({ isOpen, setIsOpen }) {
  const { user, tenant } = useAuth();
  const isAdmin = user?.role === 'SUPER_ADMIN';
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!tenant || isAdmin) return;
      try {
        const stats = await api.getPendingOrdersStats(tenant.id);
        if (!cancelled) setPendingCount((stats.pending || 0) + (stats.received || 0));
      } catch {
        // silent
      }
    };
    load();
    const interval = setInterval(load, 60000); // refresh every minute
    return () => { cancelled = true; clearInterval(interval); };
  }, [tenant, isAdmin]);

  const navigation = isAdmin ? [
    { name: 'Admin Dashboard', href: '/', icon: FiHome },
    { name: 'Tenant Management', href: '/admin/tenants', icon: FiBriefcase },
    { name: 'User Management', href: '/admin/users', icon: FiUsers },
    { name: 'System Settings', href: '/settings', icon: FiSettings },
  ] : [
    { name: 'Dashboard', href: '/', icon: FiHome },
    { name: 'Bound Book', href: '/bound-book', icon: FiBook },
    { name: 'Acquisition', href: '/acquisition', icon: FiDownload },
    { name: 'Incoming Orders', href: '/incoming-orders', icon: FiInbox, badge: pendingCount > 0 ? pendingCount : null },
    { name: 'Disposition', href: '/disposition', icon: FiUpload },
    { name: 'Contacts', href: '/contacts', icon: FiUsers },
    { name: 'Lookups', href: '/lookups', icon: FiDatabase },
    ...(user?.role === 'OWNER' ? [{ name: 'Users', href: '/users', icon: FiUserCheck }] : []),
    { name: 'Settings', href: '/settings', icon: FiSettings },
  ];

  return (
    <>
      <div className={cn(
        "fixed inset-0 z-40 bg-slate-900/80 transition-opacity lg:hidden",
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      )} onClick={() => setIsOpen(false)} />

      <div className={cn(
        "fixed inset-y-0 left-0 z-50 w-72 transform bg-white transition-transform duration-300 ease-in-out dark:bg-slate-900 lg:static lg:translate-x-0 border-r border-slate-200 dark:border-slate-800",
        isOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex h-16 shrink-0 items-center px-6 border-b border-slate-200 dark:border-slate-800">
          {tenant?.logo_url ? (
            <img
              src={tenant.logo_url}
              alt={tenant.company_name || 'Logo'}
              className="h-[50px] w-[200px] object-contain"
            />
          ) : (
            <>
              <SafeIcon icon={FiShield} className="h-8 w-8 text-primary-600" />
              <span className="ml-3 text-lg font-bold tracking-tight text-slate-900 dark:text-white">
                FFL A&D Book
              </span>
            </>
          )}
        </div>

        <nav className="flex flex-1 flex-col px-6 py-4">
          <ul role="list" className="flex flex-1 flex-col gap-y-7">
            <li>
              <ul role="list" className="-mx-2 space-y-1">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <NavLink
                      to={item.href}
                      end={item.href === '/'}
                      className={({ isActive }) => cn(
                        isActive
                          ? 'bg-slate-50 text-primary-600 dark:bg-slate-800 dark:text-primary-400'
                          : 'text-slate-700 hover:text-primary-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800',
                        'group flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors'
                      )}
                    >
                      <SafeIcon icon={item.icon} className="h-6 w-6 shrink-0" aria-hidden="true" />
                      <span className="flex-1">{item.name}</span>
                      {item.badge != null && (
                        <span className="inline-flex items-center justify-center rounded-full bg-primary-600 px-2 py-0.5 text-xs font-bold text-white">
                          {item.badge}
                        </span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </li>
            
            <li className="mt-auto">
              {isAdmin && (
                <div className="mb-4 rounded-md bg-primary-50 p-2 dark:bg-primary-900/20">
                  <p className="text-[10px] font-bold uppercase text-primary-700 dark:text-primary-400">System Admin Mode</p>
                </div>
              )}
              <div className="text-xs font-semibold leading-6 text-slate-400 dark:text-slate-500">
                Compliance Engine v1.3.0
              </div>
            </li>
          </ul>
        </nav>
      </div>
    </>
  );
}